import { useState } from 'react';
import Layout from '../../components/feature/Layout';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { reportsWeeklyData, reportsMonthlyData, topCampaignReport, comparisonData, sourceReportData } from '../../mocks/reports';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-xl p-3 shadow-hover text-[12px]">
      <p className="font-semibold text-charcoal-500 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color || p.stroke }} className="font-medium">{p.name}: {typeof p.value === 'number' && p.value > 1000 ? `₺${p.value.toLocaleString()}` : p.value}</p>
      ))}
    </div>
  );
};

function ComparisonCard({ title, current, previous, unit = '' }: { title: string; current: number; previous: number; unit?: string }) {
  const diff = current - previous;
  const pct = ((diff / previous) * 100).toFixed(1);
  const positive = diff >= 0;

  return (
    <div className="bg-white rounded-2xl shadow-card p-5">
      <p className="text-[12px] text-charcoal-300 mb-3">{title}</p>
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[26px] font-bold text-charcoal-600 leading-tight">
            {unit}{current.toLocaleString()}
          </p>
          <p className="text-[11px] text-charcoal-300 mt-1">Önceki: {unit}{previous.toLocaleString()}</p>
        </div>
        <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-semibold ${positive ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
          <i className={`${positive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[12px]`} />
          {Math.abs(parseFloat(pct))}%
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const [period, setPeriod] = useState<'weekly' | 'monthly'>('monthly');
  const chartData = period === 'weekly' ? reportsWeeklyData : reportsMonthlyData;
  const xKey = period === 'weekly' ? 'week' : 'month';

  return (
    <Layout>
      <div className="space-y-5">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-white rounded-xl border border-border p-1 shadow-card">
            {(['weekly', 'monthly'] as const).map(p => (
              <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-1.5 rounded-lg text-[13px] font-medium transition-all cursor-pointer whitespace-nowrap ${period === p ? 'bg-champagne-500 text-white' : 'text-charcoal-400 hover:text-charcoal-600'}`}>
                {p === 'weekly' ? 'Haftalık' : 'Aylık'}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-2 h-[38px] px-4 bg-white border border-border rounded-xl text-[13px] text-charcoal-500 hover:border-champagne-400 transition-colors cursor-pointer whitespace-nowrap shadow-card">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-red-400 text-[14px]" /></div>
              PDF İndir
            </button>
            <button className="flex items-center gap-2 h-[38px] px-4 bg-champagne-500 text-white rounded-xl text-[13px] font-medium hover:bg-champagne-600 transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-line text-[14px]" /></div>
              CSV Dışa Aktar
            </button>
          </div>
        </div>

        {/* Comparison: This Week vs Last Week */}
        <div>
          <p className="text-[12px] font-semibold text-charcoal-300 uppercase tracking-wide mb-3">Bu Hafta vs Geçen Hafta</p>
          <div className="grid grid-cols-4 gap-4">
            <ComparisonCard title="Ziyaretçi" current={comparisonData.thisWeek.visitors} previous={comparisonData.lastWeek.visitors} />
            <ComparisonCard title="Lead" current={comparisonData.thisWeek.leads} previous={comparisonData.lastWeek.leads} />
            <ComparisonCard title="Dönüşüm" current={comparisonData.thisWeek.conversions} previous={comparisonData.lastWeek.conversions} />
            <ComparisonCard title="Tahminî Gelir" current={comparisonData.thisWeek.revenue} previous={comparisonData.lastWeek.revenue} unit="₺" />
          </div>
        </div>

        {/* Comparison: This Month vs Last Month */}
        <div>
          <p className="text-[12px] font-semibold text-charcoal-300 uppercase tracking-wide mb-3">Bu Ay vs Geçen Ay</p>
          <div className="grid grid-cols-4 gap-4">
            <ComparisonCard title="Ziyaretçi" current={comparisonData.thisMonth.visitors} previous={comparisonData.lastMonth.visitors} />
            <ComparisonCard title="Lead" current={comparisonData.thisMonth.leads} previous={comparisonData.lastMonth.leads} />
            <ComparisonCard title="Dönüşüm" current={comparisonData.thisMonth.conversions} previous={comparisonData.lastMonth.conversions} />
            <ComparisonCard title="Tahminî Gelir" current={comparisonData.thisMonth.revenue} previous={comparisonData.lastMonth.revenue} unit="₺" />
          </div>
        </div>

        {/* Main Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 bg-white rounded-2xl shadow-card p-6">
            <div className="mb-5">
              <h3 className="text-[15px] font-semibold text-charcoal-600">Gelir Trendi</h3>
              <p className="text-[12px] text-charcoal-300 mt-0.5">Kampanya bazlı tahminî gelir</p>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#A0A0A0' }} axisLine={false} tickLine={false} tickFormatter={v => `₺${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="revenue" name="Gelir" stroke="#D4AF37" strokeWidth={2.5} fill="url(#revGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl shadow-card p-6">
            <div className="mb-5">
              <h3 className="text-[15px] font-semibold text-charcoal-600">Lead & Dönüşüm</h3>
              <p className="text-[12px] text-charcoal-300 mt-0.5">Aylık lead ve dönüşüm sayıları</p>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={reportsMonthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="leads" name="Lead" fill="#F4C2C2" radius={[3, 3, 0, 0]} />
                <Bar dataKey="conversions" name="Dönüşüm" fill="#D4AF37" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Campaigns Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-[15px] font-semibold text-charcoal-600">En İyi Kampanyalar</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAF8F4] border-b border-border">
                  {['Sıra', 'Kampanya', 'Gelir', 'Lead', 'Dönüşüm Oranı', 'Ortalama Gelir', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-charcoal-300 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topCampaignReport.map((row, idx) => (
                  <tr key={row.rank} className={`border-b border-border hover:bg-[#FAF8F4] transition-colors ${idx % 2 === 0 ? '' : 'bg-[#FDFCFA]'}`}>
                    <td className="px-4 py-3">
                      <span className={`w-7 h-7 flex items-center justify-center rounded-full text-[12px] font-bold ${row.rank === 1 ? 'bg-champagne-100 text-champagne-700' : row.rank === 2 ? 'bg-charcoal-100 text-charcoal-500' : row.rank === 3 ? 'bg-orange-50 text-orange-500' : 'bg-[#FAF8F4] text-charcoal-400'}`}>
                        {row.rank}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[13px] font-semibold text-charcoal-600">{row.campaign}</td>
                    <td className="px-4 py-3 text-[13px] font-bold text-champagne-600">₺{row.revenue.toLocaleString()}</td>
                    <td className="px-4 py-3 text-[13px] text-charcoal-500">{row.leads}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-charcoal-100 rounded-full h-1.5 max-w-[60px]">
                          <div className="h-1.5 rounded-full bg-champagne-500" style={{ width: `${(row.convRate / 12) * 100}%` }} />
                        </div>
                        <span className="text-[12px] font-semibold text-champagne-600">{row.convRate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-charcoal-500">₺{row.avgRevenue}</td>
                    <td className="px-4 py-3">
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-champagne-50 text-charcoal-300 hover:text-champagne-600 transition-colors cursor-pointer">
                        <i className="ri-eye-line text-[14px]" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Source Performance */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-[15px] font-semibold text-charcoal-600">Kaynak Performansı</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAF8F4] border-b border-border">
                  {['Kaynak', 'Lead', 'Gelir', 'ROI %'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-charcoal-300 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sourceReportData.map((row, idx) => (
                  <tr key={row.source} className={`border-b border-border hover:bg-[#FAF8F4] transition-colors ${idx % 2 === 0 ? '' : 'bg-[#FDFCFA]'}`}>
                    <td className="px-4 py-3 text-[13px] font-semibold text-charcoal-600">{row.source}</td>
                    <td className="px-4 py-3 text-[13px] text-charcoal-500">{row.leads}</td>
                    <td className="px-4 py-3 text-[13px] font-bold text-champagne-600">₺{row.revenue.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {row.roi > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-600">
                          <i className="ri-arrow-up-line text-[11px]" />{row.roi}%
                        </span>
                      ) : (
                        <span className="text-[12px] text-charcoal-300">Organik</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
